/**
 * 워크스페이스 표식. 이미지가 있으면 그 면이고, 없으면 색 타일 위의 아이콘이다.
 * 이름 글자는 그리지 않는다. 스위처·설정·디자인 시스템이 같이 쓴다.
 */

import { Badge, Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowUpload16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { useRef, useState, type ChangeEvent } from "react";
import { DialogField } from "@/components/app-dialog";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { TeamAppearanceFields, TeamMark } from "@/features/resources/team-appearance";
import type { WorkspaceMembership } from "@/engine/workspace-seeds";
import { WORKSPACE_IMAGE_MAX, WORKSPACE_MEMBERSHIP_LABEL, workspaceRowTitle } from "./workspace-face";

const useStyles = makeStyles({
  frame: {
    position: "relative",
    display: "inline-flex",
    flexShrink: 0,
    overflow: "visible",
  },
  face: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  dot: {
    position: "absolute",
    top: "-1px",
    right: "-1px",
    width: "6px",
    height: "6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteRedForeground1,
    boxShadow: `0 0 0 1px ${tokens.colorNeutralBackground1}`,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  profileRow: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    minWidth: 0,
    "@media (max-width: 560px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
    },
  },
  markPreviewWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    borderRadius: "14px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  },
  avatarActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    minWidth: 0,
    flex: 1,
  },
  avatarButtons: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  avatarHint: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  imageBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorPaletteBlueForeground2,
    backgroundColor: tokens.colorPaletteBlueBackground2,
    padding: "3px 8px",
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightRegular,
  },
  file: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    border: 0,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    minWidth: 0,
  },
  fieldError: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorPaletteRedForeground1,
    marginTop: "2px",
  },
  readOnlyCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 16px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  livePreviewCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "10px 14px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  livePreviewLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
  },
  livePreviewTag: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  livePreviewName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  livePreviewRoleBadge: {
    flexShrink: 0,
  },
  appearanceWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minWidth: 0,
  },
  readName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  readEmpty: {
    color: tokens.colorNeutralForeground3,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    minWidth: "240px",
    maxWidth: "280px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    height: "32px",
    padding: "0 6px",
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground1,
  },
  rowCurrent: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  rowName: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  role: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  rule: {
    height: "1px",
    margin: "4px 6px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
});

function inkOn(hex: string): string {
  const n = Number.parseInt(hex.replace("#", ""), 16);
  if (!Number.isFinite(n)) return "#fff";
  const y = (((n >> 16) & 255) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000;
  return y > 150 ? "#1b1b1b" : "#fff";
}

export function WorkspaceMark({
  icon,
  color,
  imageUrl,
  size = 20,
  pending = false,
}: {
  icon: string;
  color: string;
  imageUrl: string | null;
  size?: number;
  pending?: boolean;
}) {
  const styles = useStyles();
  const radius = size >= 56 ? "14px" : size >= 28 ? tokens.borderRadiusLarge : tokens.borderRadiusMedium;
  return (
    <span className={styles.frame} style={{ width: size, height: size }} aria-hidden>
      {imageUrl ? (
        <span className={styles.face} style={{ width: size, height: size, borderRadius: radius }}>
          <img src={imageUrl} alt="" className={styles.image} />
        </span>
      ) : (
        <span
          className={styles.face}
          style={{ width: size, height: size, borderRadius: radius, backgroundColor: color, color: inkOn(color) }}
        >
          <TeamMark icon={icon} color={inkOn(color)} size={Math.max(12, Math.round(size * 0.55))} />
        </span>
      )}
      {pending ? <span className={styles.dot} /> : null}
    </span>
  );
}

function readWorkspaceImage(file: File): Promise<{ url: string } | { error: string }> {
  if (!file.type.startsWith("image/")) return Promise.resolve({ error: "이미지 파일을 올려 주세요." });
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve({ error: "이미지를 열 수 없습니다." });
    reader.onload = () => {
      const src = typeof reader.result === "string" ? reader.result : "";
      const image = new Image();
      image.onerror = () => resolve({ error: "이미지를 열 수 없습니다." });
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 128;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx || image.width < 1 || image.height < 1) {
          resolve({ error: "이미지를 열 수 없습니다." });
          return;
        }
        const scale = Math.max(size / image.width, size / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
        const url = canvas.toDataURL("image/jpeg", 0.85);
        if (url.length > WORKSPACE_IMAGE_MAX) resolve({ error: "이미지가 너무 큽니다." });
        else resolve({ url });
      };
      image.src = src;
    };
    reader.readAsDataURL(file);
  });
}

export function WorkspaceProfileFields({
  name,
  description,
  icon,
  color,
  imageUrl,
  readOnly = false,
  autoFocusName = false,
  onNameChange,
  onDescriptionChange,
  onIconChange,
  onColorChange,
  onImageChange,
  nameError,
  roleLabel,
  onEnterSubmit,
}: {
  name: string;
  description: string;
  icon: string;
  color: string;
  imageUrl: string | null;
  readOnly?: boolean;
  autoFocusName?: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onIconChange: (value: string) => void;
  onColorChange: (value: string) => void;
  onImageChange: (value: string | null) => void;
  nameError?: string | null;
  roleLabel?: string;
  onEnterSubmit?: () => void;
}) {
  const styles = useStyles();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const trimmed = description.trim();

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    void readWorkspaceImage(file).then((result) => {
      if ("error" in result) {
        setError(result.error);
        return;
      }
      setError(null);
      onImageChange(result.url);
    });
  };

  return (
    <div className={styles.container}>
      {/* 1. 기본 정보 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>기본 정보</div>
          <Caption1 className={styles.cardSubtitle}>
            워크스페이스의 대표 이미지와 이름을 관리합니다.
          </Caption1>
        </div>

        <div className={styles.profileRow}>
          <div className={styles.markPreviewWrap}>
            <WorkspaceMark icon={icon} color={color} imageUrl={imageUrl} size={68} />
          </div>
          <div className={styles.avatarActions}>
            {!readOnly ? (
              <div className={styles.avatarButtons}>
                <FuiButton
                  appearance="secondary"
                  density="dialog"
                  icon={<ArrowUpload16Regular />}
                  onClick={() => fileRef.current?.click()}
                >
                  {imageUrl ? "이미지 변경" : "이미지 업로드"}
                </FuiButton>
                {imageUrl ? (
                  <FuiButton
                    appearance="subtle"
                    density="dialog"
                    icon={<Dismiss16Regular />}
                    onClick={() => {
                      setError(null);
                      onImageChange(null);
                    }}
                  >
                    이미지 제거
                  </FuiButton>
                ) : null}
                <input
                  ref={fileRef}
                  className={styles.file}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label="워크스페이스 대표 이미지"
                  onChange={onFile}
                />
              </div>
            ) : null}
            {imageUrl ? (
              <span className={styles.imageBadge}>
                대표 이미지 설정됨 (심볼 대신 표시)
              </span>
            ) : null}
            <Caption1 className={styles.avatarHint}>
              권장: 256×256px 이상의 정사각형 이미지 (최대 120KB, PNG/JPG/WebP)
            </Caption1>
            {error ? (
              <Caption1 className={styles.error} role="alert">
                {error}
              </Caption1>
            ) : null}
          </div>
        </div>

        <div className={styles.formGroup}>
          {readOnly ? (
            <div className={styles.readOnlyCard}>
              <DialogField label="워크스페이스 이름">
                <span className={styles.readName}>{name}</span>
              </DialogField>
              <DialogField label="설명">
                <span className={mergeClasses(styles.readName, !trimmed && styles.readEmpty)}>
                  {trimmed || "설명이 없습니다."}
                </span>
              </DialogField>
            </div>
          ) : (
            <>
              <DialogField label="이름" required>
                <FuiInput
                  density="dialog"
                  value={name}
                  placeholder="예: 웨이브엑스 본사"
                  autoFocus={autoFocusName}
                  onChange={(_, data) => onNameChange(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onEnterSubmit?.();
                  }}
                />
                {nameError ? <Caption1 className={styles.fieldError}>{nameError}</Caption1> : null}
              </DialogField>
              <DialogField label="설명 (선택)">
                <FuiInput
                  density="dialog"
                  value={description}
                  placeholder="조직 또는 프로젝트 플랜 묶음"
                  onChange={(_, data) => onDescriptionChange(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onEnterSubmit?.();
                  }}
                />
                <Caption1 className={styles.avatarHint}>
                  워크스페이스 전환기 및 초대 화면에서 멤버들에게 표시됩니다.
                </Caption1>
              </DialogField>
            </>
          )}
        </div>
      </div>

      {/* 2. 심볼 및 테마 컬러 카드 */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>심볼 및 테마 컬러</div>
          <Caption1 className={styles.cardSubtitle}>
            대표 이미지가 없거나 사이드바 및 헤더에서 워크스페이스를 식별할 때 표시되는 대표 심볼과 컬러입니다.
          </Caption1>
        </div>

        {/* 라이브 미리보기 바 */}
        <div className={styles.livePreviewCard}>
          <div className={styles.livePreviewLeft}>
            <span className={styles.livePreviewTag}>사이드바 미리보기</span>
            <WorkspaceMark icon={icon} color={color} imageUrl={null} size={24} />
            <span className={styles.livePreviewName}>{name.trim() || "새 워크스페이스"}</span>
          </div>
          {roleLabel ? (
            <Badge size="small" appearance="tint" color="informative" className={styles.livePreviewRoleBadge}>
              {roleLabel}
            </Badge>
          ) : null}
        </div>

        {!readOnly ? (
          <div className={styles.appearanceWrap}>
            <TeamAppearanceFields icon={icon} color={color} onIconChange={onIconChange} onColorChange={onColorChange} />
          </div>
        ) : (
          <Caption1 className={styles.avatarHint}>
            워크스페이스 설정을 변경하려면 관리자 또는 소유자 권한이 필요합니다.
          </Caption1>
        )}
      </div>
    </div>
  );
}

export type WorkspaceSwitchRowModel = {
  id: string;
  name: string;
  icon: string;
  color: string;
  imageUrl: string | null;
  role: WorkspaceMembership;
  ownerName: string | null;
  ownerEmail: string | null;
  current?: boolean;
};

/** 스위처 목록의 보기. 소유와 참여 사이만 선을 둔다. */
export function WorkspaceSwitchRows({ owned, joined }: { owned: WorkspaceSwitchRowModel[]; joined: WorkspaceSwitchRowModel[] }) {
  const styles = useStyles();
  const row = (item: WorkspaceSwitchRowModel) => (
    <div key={item.id} className={mergeClasses(styles.row, item.current && styles.rowCurrent)} title={workspaceRowTitle(item.name, item.role, item.ownerName, item.ownerEmail)}>
      <WorkspaceMark icon={item.icon} color={item.color} imageUrl={item.imageUrl} size={16} />
      <span className={styles.rowName}>{item.name}</span>
      <span className={styles.role}>{WORKSPACE_MEMBERSHIP_LABEL[item.role]}</span>
    </div>
  );
  return (
    <div className={styles.list}>
      {owned.map(row)}
      {owned.length > 0 && joined.length > 0 ? <div className={styles.rule} /> : null}
      {joined.map(row)}
    </div>
  );
}
